import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Drawer,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  withStyles,
} from "@material-ui/core";
import InternalChat from "../components/InternalChat";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";

import BackdropLoading from "../components/BackdropLoading";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import MainListItems from "./MainListItems";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import UserLanguageSelector from "../components/UserLanguageSelector";
import toastError from "../errors/toastError";
import { i18n } from "../translate/i18n";

import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import { Refresh } from "iconsax-react";
import MenuOpenIcon from "@material-ui/icons/MenuOpen";
import logo from "../assets/logo.png";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import { socketConnection } from "../services/socket";
import ColorModeContext from "./themeContext";

const backendUrl = getBackendUrl();
const ICON_RAIL_WIDTH = 68;
const SIDEBAR_EXPANDED_WIDTH = 260;
const SIDEBAR_STORAGE_KEY = "sidebarExpanded";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.fancyBackground,
    fontFamily: '"Inter", "Poppins", sans-serif',
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: "10px",
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main,
    },
    "& .MuiButton-root": {
      borderRadius: "10px",
    },
  },

  // AppBar - now a clean white/dark bar (left is set dynamically via inline style)
  appBar: {
    position: "fixed",
    top: 0,
    right: 0,
    height: 60,
    zIndex: theme.zIndex.drawer + 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    backgroundColor: theme.palette.appBar?.background || "#FFFFFF",
    color: theme.palette.appBar?.text || "#1E293B",
    boxShadow: theme.palette.appBar?.shadow || "0 1px 3px 0 rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    [theme.breakpoints.down("sm")]: {
      left: 0,
    },
  },
  appBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  appBarRight: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  welcomeText: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.appBar?.text || "#1E293B",
    fontFamily: '"Inter", "Poppins", sans-serif',
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "& b": {
      fontWeight: 600,
    },
  },

  // Content area (marginLeft set dynamically via inline style)
  content: {
    flex: 1,
    transition: "margin-left 0.3s ease",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0 !important",
    },
  },

  // AppBar icon buttons
  appBarIconButton: {
    color: theme.palette.appBar?.text || "#64748B",
    padding: 8,
    borderRadius: 10,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F1F5F9",
    },
    "& svg": {
      width: 20,
      height: 20,
    },
  },

  // User avatar in AppBar
  avatar2: {
    width: 36,
    height: 36,
    cursor: "pointer",
    borderRadius: "50%",
    border: `2px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0"}`,
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  onlineBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#22C55E",
      color: "#22C55E",
      boxShadow: `0 0 0 2px ${theme.palette.appBar?.background || "#FFFFFF"}`,
      width: 10,
      height: 10,
      borderRadius: "50%",
    },
  },

  // Mobile drawer
  mobileDrawer: {
    "& .MuiDrawer-paper": {
      width: ICON_RAIL_WIDTH,
      backgroundColor: theme.palette.sidebar?.background || "#1B2431",
      border: "none",
    },
  },

  // Hamburger button for mobile
  menuButton: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
    },
  },

  // Hide sidebar on mobile
  sidebarDesktop: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

const LoggedInLayout = ({ children, themeToggle }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, showDialogButton } = useContext(AuthContext);
  const { user, socket } = useContext(AuthContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const sidebarWidth = sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : ICON_RAIL_WIDTH;

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);

  const settings = useSettings();

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      // Desktop - sidebar always visible
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu]);

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;

    const socket = socketConnection({ companyId, userId: user.id });
    if (!socket) {
      return () => {};
    }
    const ImageUrl = user.profileImage;
    if (ImageUrl !== undefined && ImageUrl !== null)
      setProfileUrl(
        `${backendUrl}/public/company${companyId}/user/${ImageUrl}`
      );
    else setProfileUrl(`${process.env.FRONTEND_URL}/nopicture.png`);

    socket.on(`company-${companyId}-auth`, (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    });

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarExpandToggle = () => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "true" : "false");
      } catch (e) {}
      return next;
    });
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      {/* Desktop Sidebar - always visible */}
      <div className={classes.sidebarDesktop}>
        <MainListItems
          sidebarExpanded={sidebarExpanded}
          sidebarWidth={sidebarWidth}
        />
      </div>

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          className={classes.mobileDrawer}
          ModalProps={{ keepMounted: true }}
        >
          <MainListItems sidebarExpanded={false} sidebarWidth={ICON_RAIL_WIDTH} />
        </Drawer>
      )}

      {/* AppBar */}
      <div
        className={classes.appBar}
        style={{
          left: greaterThenSm ? sidebarWidth : 0,
          transition: "left 0.3s ease",
        }}
      >
        <div className={classes.appBarLeft}>
          {/* Mobile hamburger */}
          <IconButton
            className={classes.menuButton}
            onClick={handleMobileDrawerToggle}
            style={{ color: theme.palette.appBar?.text || "#1E293B" }}
          >
            <MenuIcon />
          </IconButton>

          {/* Expandir/recolher menu (desktop) - ícone discreto */}
          {greaterThenSm && (
            <Tooltip title={sidebarExpanded ? "Recolher menu" : "Expandir menu"} placement="bottom" arrow>
              <IconButton
                onClick={handleSidebarExpandToggle}
                aria-label={sidebarExpanded ? "Recolher menu" : "Expandir menu"}
                className={classes.appBarIconButton}
                style={{ color: theme.palette.appBar?.text || "#64748B" }}
                size="small"
              >
                <MenuOpenIcon style={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Welcome text */}
          <Typography className={classes.welcomeText}>
            {greaterThenSm &&
              user?.profile === "admin" &&
              user?.company?.dueDate ? (
              <>
                {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                {i18n.t("mainDrawer.appBar.user.messageEnd")}{" "}
                <b>{user?.company?.name}</b>! (
                {i18n.t("mainDrawer.appBar.user.active")}{" "}
                {dateToClient(user?.company?.dueDate)})
              </>
            ) : (
              <>
                {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                {i18n.t("mainDrawer.appBar.user.messageEnd")}{" "}
                <b>{user?.company?.name}</b>!
              </>
            )}
          </Typography>
        </div>

        <div className={classes.appBarRight}>
          <UserLanguageSelector />

          <NotificationsVolume setVolume={setVolume} volume={volume} />

          <IconButton
            onClick={handleRefreshPage}
            aria-label={i18n.t("mainDrawer.appBar.refresh")}
            className={classes.appBarIconButton}
          >
            <Refresh />
          </IconButton>

          {user.id && <NotificationsPopOver volume={volume} />}

          <AnnouncementsPopover />

          <ChatPopover />

          <div>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              className={classes.onlineBadge}
              onClick={handleMenu}
            >
              <Avatar
                alt={user.name}
                className={classes.avatar2}
                src={profileUrl}
              />
            </Badge>

            <UserModal
              open={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
              userId={user?.id}
            />

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={menuOpen}
              onClose={handleCloseMenu}
              PaperProps={{
                style: {
                  borderRadius: 12,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
                  marginTop: 8,
                  minWidth: 180,
                },
              }}
            >
              <MenuItem
                onClick={handleOpenUserModal}
                style={{ fontFamily: '"Inter", sans-serif', fontSize: 14 }}
              >
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem
                onClick={handleClickLogout}
                style={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 14,
                  color: "#ef5350",
                }}
              >
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main
        className={classes.content}
        style={{
          marginLeft: greaterThenSm ? sidebarWidth : 0,
        }}
      >
        {showDialogButton && <InternalChat />}
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
